import { REQUEST_STATUS } from "lib/types";
import { createReducer, reduce } from "re-reduced";

import actions from "./actions";
import { CommercialProduct, CoreState, Product } from "./types";

// const DEFAULT_PRODUCTS = ["flat", "off_peak", "freestyle"] as Product[]
// Freestyle is currently disabled, revert defaults to the above when it is available again.
export const DEFAULT_PRODUCTS = ["flat", "off_peak"] as Product[];
export const COMMERCIAL_PRODUCTS = ["business"] as CommercialProduct[];

const INITIAL_STATE: CoreState = {
  validateAddressRequestStatus: REQUEST_STATUS.Idle,
  createFlickSignUpRequestStatus: REQUEST_STATUS.Idle,
  updateFlickSignUpRequestStatus: REQUEST_STATUS.Idle,
  flickSignUpRequestStatus: REQUEST_STATUS.Idle,
  windcaveTransactionRequestStatus: REQUEST_STATUS.Idle,
  windcaveAuthorisationRequestStatus: REQUEST_STATUS.Idle,
  billingEstimateRequestStatus: REQUEST_STATUS.Idle,
  productDataRequestStatus: REQUEST_STATUS.Idle,
  eligibleAddresses: {
    data: [],
    included: [],
  },
  flickUserId: null,
  user: {
    checks: {
      marketingAccepted: false,
      smsSwitchNotificationsAccepted: false,
      termsAccepted: false,
      creditCheckAccepted: false,
      isCommercialAccount: undefined,
    },
    details: {
      emailAddress: "",
      firstName: "",
      lastName: "",
      prefName: "",
      mobileNumber: "",
      homeNumber: "",
      companyName: "",
      product: undefined,
    },
    payment: {
      paymentType: undefined,
      billingPeriod: undefined,
    },
    property: {
      usageType: undefined,
      medicallyDependent: undefined,
      situation: undefined,
      moveDate: undefined,
      switchDate: undefined,
    },
  },
  estimatedUsageType: "low",
  signUpState: "0",
  currentFormStep: "/join",
  partialSignUpDialogOpen: false,
  promoCode: undefined,
  leadSource: undefined,
  availableProducts: DEFAULT_PRODUCTS,
};

const reducer = createReducer<CoreState>(
  [
    // User
    reduce(actions.setUserDetails, (state, payload) => ({
      ...state,
      user: {
        ...state.user,
        details: payload,
      },
    })),
    reduce(actions.setUserProperty, (state, payload) => ({
      ...state,
      user: {
        ...state.user,
        property: payload,
      },
    })),
    reduce(actions.setUserChecks, (state, payload) => ({
      ...state,
      user: {
        ...state.user,
        checks: payload,
      },
    })),
    reduce(actions.setUserPayment, (state, payload) => ({
      ...state,
      user: {
        ...state.user,
        payment: payload,
      },
    })),

    reduce(actions.setUserBillingPeriod, (state, payload) => ({
      ...state,
      user: {
        ...state.user,
        payment: {
          ...state.user.payment,
          billingPeriod: payload,
        },
      },
    })),
    reduce(actions.setUserProduct, (state, payload) => ({
      ...state,
      user: {
        ...state.user,
        details: {
          ...state.user.details,
          product: payload,
        },
      },
    })),

    reduce(actions.setCurrentFormStep, (state, payload) => ({
      ...state,
      currentFormStep: payload,
    })),
    reduce(actions.fetchProductData.success, (state, payload) => ({
      ...state,
      productDataRequestStatus: REQUEST_STATUS.Fulfilled,
      product: payload,
    })),
    reduce(actions.fetchProductData.failure, (state) => ({
      ...state,
      productDataRequestStatus: REQUEST_STATUS.Failed,
    })),
    reduce(actions.fetchProductData.request, (state) => ({
      ...state,
      productDataRequestStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.setPartialSignUpDialogState, (state, payload) => ({
      ...state,
      partialSignUpDialogOpen: payload,
    })),

    reduce(actions.setPromoCode, (state, payload) => ({
      ...state,
      promoCode: payload,
    })),

    reduce(actions.setAvailableProducts, (state, payload) => ({
      ...state,
      availableProducts: payload,
    })),
    // ---------------------Address-------------------------------
    // -----------------------------------------------------------

    reduce([actions.validateAddress.request], (state) => ({
      ...state,
      validateAddressRequestStatus: REQUEST_STATUS.Pending,
      selectedAddress: undefined,
    })),
    reduce([actions.validateAddress.failure], (state) => ({
      ...state,
      validateAddressRequestStatus: REQUEST_STATUS.Failed,
    })),
    reduce([actions.validateAddress.success], (state, payload) => ({
      ...state,
      validateAddressRequestStatus: REQUEST_STATUS.Fulfilled,
      eligibleAddresses: payload.eligibleAddresses,
    })),
    reduce(actions.selectAddress, (state, payload) => ({
      ...state,
      validateAddressRequestStatus: REQUEST_STATUS.Fulfilled,
      selectedAddress: payload.address,
      selectedEligibilityType: payload.eligibility,
    })),
    reduce(actions.fetchAvailableProducts.success, (state, payload) => ({
      ...state,
      validateAddressRequestStatus: REQUEST_STATUS.Fulfilled,
      availableProducts: payload?.data?.map(
        (plan) => plan.attributes.product_name,
      ),
    })),
    reduce(actions.updateEligibility, (state, payload) => ({
      ...state,
      selectedEligibilityType: payload.eligibility,
      billingEstimateRequestStatus: REQUEST_STATUS.Fulfilled,
    })),
    reduce(actions.clearAddress, (state) => ({
      ...state,
      billingEstimateRequestStatus: REQUEST_STATUS.Idle,
    })),
    reduce(actions.fetchBillingEstimate.request, (state) => ({
      ...state,
      billingEstimateRequestStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.fetchBillingEstimate.failure, (state) => ({
      ...state,
      billingEstimateRequestStatus: REQUEST_STATUS.Failed,
      billEstimates: { data: [] },
      availableProducts: DEFAULT_PRODUCTS,
    })),
    reduce(actions.fetchBillingEstimate.success, (state, payload) => ({
      ...state,
      billingEstimateRequestStatus: REQUEST_STATUS.Fulfilled,
      billEstimates: payload,
      estimatedUsageType: payload.data[0].attributes.usage_type,
      availableProducts: payload.data
        ? payload.data?.map((plan) => plan.attributes.product_name)
        : [],
    })),

    // ------------------------Payment----------------------------
    // -----------------------------------------------------------

    reduce(actions.createWindcaveTransaction.request, (state) => ({
      ...state,
      windcaveTransactionRequestStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.createWindcaveTransaction.failure, (state) => ({
      ...state,
      windcaveTransactionRequestStatus: REQUEST_STATUS.Failed,
    })),
    reduce(actions.createWindcaveTransaction.success, (state, payload) => ({
      ...state,
      windcaveSignUpId: payload.data.attributes.sign_up_id,
      transactionId: payload.data.id,
      windcaveTransactionRequestStatus: REQUEST_STATUS.Fulfilled,
    })),
    reduce(actions.checkSuccessfulAuthorisation.request, (state) => ({
      ...state,
      windcaveAuthorisationRequestStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.checkSuccessfulAuthorisation.failure, (state) => ({
      ...state,
      windcaveAuthorisationRequestStatus: REQUEST_STATUS.Failed,
    })),
    reduce(actions.checkSuccessfulAuthorisation.success, (state, payload) => ({
      ...state,
      windcaveSignUpId: payload.data.attributes.sign_up_id,
      windcaveAuthorisationRequestStatus: REQUEST_STATUS.Fulfilled,
    })),

    // -------------------------Account---------------------------
    // -----------------------------------------------------------

    reduce(actions.createFlickSignUp.request, (state) => ({
      ...state,
      flickUserId: null,
      createFlickSignUpRequestStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.createFlickSignUp.failure, (state, payload) => ({
      ...state,
      createFlickSignUpRequestStatus: REQUEST_STATUS.Failed,
      flickAccountError: payload,
    })),
    reduce(actions.createFlickSignUp.success, (state, payload) => ({
      ...state,
      createFlickSignUpRequestStatus: REQUEST_STATUS.Fulfilled,
      flickUserId: payload.data.id,
    })),
    reduce(actions.updateFlickSignUp.request, (state) => ({
      ...state,
      updateFlickSignUpRequestStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.updateFlickSignUp.failure, (state, payload) => ({
      ...state,
      updateFlickSignUpRequestStatus: REQUEST_STATUS.Failed,
      flickAccountError: payload,
    })),
    reduce(actions.updateFlickSignUp.success, (state) => ({
      ...state,
      updateFlickSignUpRequestStatus: REQUEST_STATUS.Fulfilled,
    })),
    reduce(actions.setFlickUserId, (state, payload) => ({
      ...state,
      flickUserId: payload,
    })),
    reduce(actions.getFlickSignUp.request, (state) => ({
      ...state,
      flickSignUpRequestStatus: REQUEST_STATUS.Pending,
    })),
    reduce(actions.getFlickSignUp.failure, (state, payload) => ({
      ...state,
      flickSignUpRequestStatus: REQUEST_STATUS.Failed,
      flickAccountError: payload,
    })),
    reduce(actions.getFlickSignUp.success, (state) => ({
      ...state,
      flickSignUpRequestStatus: REQUEST_STATUS.Fulfilled,
    })),
    reduce(actions.setFlickSignUpStateComplete, (state) => ({
      ...state,
      signUpState: "complete",
    })),
    reduce(actions.setCreationSourceIdentifier, (state) => ({
      ...state,
      creationSource: "sign_up_api_guided",
    })),

    reduce(actions.setLeadSource, (state, payload) => ({
      ...state,
      leadSource: payload,
    })),

    reduce(actions.setSignUpPaymentType, (state, payload) => ({
      ...state,
      signUpPaymentType: payload,
    })),

    // ----------------------Abandoned Cart-----------------------
    // -----------------------------------------------------------
    reduce(actions.setAddress, (state, payload) => ({
      ...state,
      selectedAddress: payload,
    })),

    reduce(actions.clearState, () => ({
      ...INITIAL_STATE,
    })),
  ],
  INITIAL_STATE,
);

export default reducer;
